import ApproversInputForm from 'components/Form/Approvers';
import Dropdown from 'components/Form/Dropdown';
import FormResult from 'components/Form/FormResult';
import InputText from 'components/Form/InputText';
import Information from 'components/Information';
import HorizontalRule from 'components/Layout/HorizontalRule';
import Stacked from 'components/Layout/Stacked';
import Modal from 'components/Modal';
import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {c, t} from 'ttag';
import {
    documentTypeCheckboxOptions,
    documentTypeLabelByType,
} from 'utils/document-type';
import {onHatsApiError} from 'utils/request-axios/utils';
import {whitelist} from 'validator';

import {useCreateApprovalFlow, useModifyApprovalFlow} from '../../services';
import {ApprovalFlow, BackendData} from '../../types';

type FormData = {
    approvers: {value: string}[];
    document_type: string;
    name: string;
};

const ALL_DOCUMENT_TYPES = 'ALL_DOCUMENT_TYPES';

const usePreparedForm = (initialData?: ApprovalFlow) => {
    const defaultValues: FormData = {
        approvers: [],
        document_type: ALL_DOCUMENT_TYPES,
        name: '',
    };

    if (initialData) {
        defaultValues.document_type =
            whitelist(initialData.documentType, 'a-z') || ALL_DOCUMENT_TYPES;
        defaultValues.name = initialData.name;
        defaultValues.approvers = initialData.steps.map((step) => ({
            value: step.teamMid,
        }));
    }

    return useForm<FormData>({defaultValues});
};
const ApprovalFlowForm: FC<{
    actionType: 'create' | 'edit';
    initialData?: ApprovalFlow;
    open: boolean;
    setOpen: (open: boolean) => void;
}> = ({actionType, initialData, open, setOpen}) => {
    const {
        data: {partnerMid, users},
        refreshData,
    } = useBackend<BackendData>();

    const methods = usePreparedForm(initialData);

    const apiCreate = useCreateApprovalFlow();
    const apiModify = useModifyApprovalFlow();
    const isProcessing = apiCreate.isPending || apiModify.isPending;

    const handleCreate = async (formData: FormData) => {
        if (formData.document_type === ALL_DOCUMENT_TYPES)
            formData.document_type = '';

        await apiCreate
            .mutateAsync({
                formData: {
                    document_type: formData.document_type,
                    name: formData.name,
                    rules: {rules: []},
                    steps: formData.approvers.map((teamMid, index) => ({
                        position: index,
                        team_mid: teamMid.value,
                    })),
                },
                partnerMid: partnerMid,
            })
            .then(async () => {
                await refreshData(window.Urls.approval_settings_view());
                handleClose();
            })
            .catch(onHatsApiError(methods));
    };
    const handleEdit = async (formData: FormData) => {
        if (!initialData)
            throw new Error('Initial data is required for editing');

        if (formData.document_type === ALL_DOCUMENT_TYPES)
            formData.document_type = '';

        await apiModify
            .mutateAsync({
                formData: {
                    document_type: formData.document_type,
                    name: formData.name,
                    rules: {rules: []},
                    steps: formData.approvers.map((teamMid, index) => ({
                        position: index,
                        team_mid: teamMid.value,
                    })),
                },
                mid: initialData.mid,
                partnerMid: partnerMid,
            })
            .then(async () => {
                await refreshData(window.Urls.approval_settings_view());
                handleClose();
            })
            .catch(onHatsApiError(methods));
    };
    const handleClose = () => {
        setOpen(false);
        methods.reset();
    };

    return (
        <FormProvider {...methods}>
            <Modal isOpen={open} onClose={handleClose}>
                <Modal.Header>
                    {actionType === 'create'
                        ? c('approval-settings').t`Create Approval Flow`
                        : c('approval-settings').t`Edit Approval Flow`}
                </Modal.Header>
                <Modal.Body className="h-auto">
                    <div id={`${actionType}-form`}>
                        <Stacked>
                            <InputText
                                label={c('approval-settings').t`Flow Name`}
                                name="name"
                                required={true}
                            />
                            <ApproversInputForm
                                label={c('approval-settings').t`Approvers`}
                                name="approvers"
                                options={users}
                                required={true}
                            />
                            <Dropdown
                                isSearchable={false}
                                label={t`Document Type`}
                                name="document_type"
                                options={[
                                    {
                                        label: documentTypeLabelByType('all'),
                                        value: ALL_DOCUMENT_TYPES,
                                    },
                                    ...documentTypeCheckboxOptions().map(
                                        (option) => ({
                                            label: option.label,
                                            value: option.name,
                                        }),
                                    ),
                                ]}
                                required={true}
                                type="select"
                            />
                            <HorizontalRule />
                            {actionType === 'edit' && (
                                <Information icon="warning" type="alert">
                                    {c('approval-settings')
                                        .t`Changing this approval flow will not affect documents it has already been assigned to.`}
                                </Information>
                            )}
                            <FormResult />
                        </Stacked>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Modal.Button
                        disabled={isProcessing}
                        kind="secondary"
                        onClick={handleClose}
                        type="cancel"
                    >
                        {t`Close`}
                    </Modal.Button>
                    <Modal.Button
                        disabled={!methods.formState.isDirty || isProcessing}
                        kind="primary"
                        onClick={methods.handleSubmit(
                            actionType === 'create' ? handleCreate : handleEdit,
                        )}
                        type="submit"
                    >
                        {actionType === 'create'
                            ? c('approval-settings').t`Create Approval Flow`
                            : c('approval-settings').t`Edit Approval Flow`}
                    </Modal.Button>
                </Modal.Footer>
            </Modal>
        </FormProvider>
    );
};

export default ApprovalFlowForm;
